import React, { PropsWithChildren, useState } from 'react';
import { ApolloClient, NormalizedCacheObject, useApolloClient } from '@apollo/client';
import Bugsnag from '@bugsnag/js';
import styled from 'styled-components';
import { Button, Spinner } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { linkTo } from '@99designs/routing';
import { CreateBriefFromProductCardDocument } from './CreateBriefFromProductCard.generated';
import type { CreateBriefFromProductCardMutationVariables } from './CreateBriefFromProductCard.generated';

interface StartBriefChildProps {
    loading: boolean;
    startBrief: () => void;
}

interface startBriefProviderProps {
    children: (props: StartBriefChildProps) => React.ReactElement;
}

interface StartBriefProps extends startBriefProviderProps {
    data: CreateBriefFromProductCardMutationVariables;
}

const createBrief = async (
    apolloClient: ApolloClient<NormalizedCacheObject>,
    data: CreateBriefFromProductCardMutationVariables
) => {
    try {
        const res = await apolloClient.mutate({
            mutation: CreateBriefFromProductCardDocument,
            variables: data,
        });

        if (res && res.data) {
            window.location.href = linkTo.brief(res.data.brief.id, 1);
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
        Bugsnag.notify(error);
    }
};

export function StartProjectBriefProvider({ children, data }: StartBriefProps) {
    // Manage loading state locally instead of using apollo
    // so the loading state persists while redirecting
    const [loading, setLoading] = useState(false);
    const apolloClient = useApolloClient() as ApolloClient<NormalizedCacheObject>;

    return children({
        loading,
        startBrief: async () => {
            setLoading(true);
            await createBrief(apolloClient, data);
            setLoading(false);
        },
    });
}

interface startContestBriefProviderProps extends startBriefProviderProps {
    category: string;
    data: {
        industry?: string;
    };
}

export function StartContestBriefProvider({
    category,
    children,
    data,
}: startContestBriefProviderProps) {
    const [loading, setLoading] = useState(false);

    return children({
        loading,
        startBrief: () => {
            setLoading(true);
            window.location.href = linkTo.launchBrief(category, data);
        },
    });
}

const StyledButton = styled(Button)`
    ${mediaQuery.minimum('normal')} {
        width: 200px;
    }
` as typeof Button;

export function StartBriefButton({
    children,
    onClick,
    loading,
}: PropsWithChildren<{
    onClick: () => void;
    loading?: boolean;
}>) {
    return (
        <StyledButton
            size="large"
            appearance="primary"
            width="fill"
            onClick={onClick}
            disabled={loading}
        >
            {loading ? <Spinner color="grey500" size="medium" inline /> : children}
        </StyledButton>
    );
}
